/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import LogoInput from '../BenefitOfferAdmin/LogoInput';
import NewsfeedButton from '../Newsfeed/Button';

const LearnSubsectionAdminModal = ({
    type,
    postSubsection = () => {},
    posting,
    preFetchedSubsection,
    subsectionLanguages,
    sections,
    languages,
    sites
}) => {
    const subsection = preFetchedSubsection ? JSON.parse(preFetchedSubsection) : null;
    const subsectionLanguageRows = JSON.parse(subsectionLanguages || '[]');
    const sectionsUnsorted = JSON.parse(sections || '[]');
    const allLanguages = JSON.parse(languages || '[]');
    const unsortedSites = JSON.parse(sites || '[]');
    const allSites = unsortedSites.sort((a, b) => (a?.topLocation?.name || '').localeCompare(b?.topLocation?.name || ''));

    const sectionsSorted = useMemo(() => {
        return [...sectionsUnsorted].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    }, [sectionsUnsorted]);

    const languageNameByCode = useMemo(() => {
        const map = new Map();
        (Array.isArray(allLanguages) ? allLanguages : Object.values(allLanguages || {})).forEach((languageItem) => {
            const code = languageItem?.languageCode ?? '';
            if (code) map.set(code, languageItem?.name ?? '');
        });
        return map;
    }, [allLanguages]);

    const editModeHasRows = Array.isArray(subsectionLanguageRows) && subsectionLanguageRows.length > 0;

    const availableLanguageCodes = useMemo(() => {
        const codes = (Array.isArray(allLanguages) ? allLanguages : Object.values(allLanguages || {}))
            .map((language) => language?.languageCode)
            .filter(Boolean);
        return Array.from(new Set(codes));
    }, [allLanguages]);

    const languageRowByCode = useMemo(() => {
        const map = new Map();
        if (editModeHasRows) {
            subsectionLanguageRows.forEach((row) => {
                if (row?.languageCode) map.set(row.languageCode, row);
            });
        }
        return map;
    }, [subsectionLanguageRows, editModeHasRows]);

    const [image, setImage] = useState(null);
    const [learnSectionCode, setLearnSectionCode] = useState('');
    const [languageCode, setLanguageCode] = useState('');
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [selectedSites, setSelectedSites] = useState([]);

    const initialized = useRef(false);

    const hideModal = () => {
        const modalWrapper = document.querySelector('.modal-wrapper');
        if (modalWrapper) modalWrapper.classList.toggle('hidden');
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        postSubsection(image, learnSectionCode, languageCode, name, content, selectedSites);
    };

    useEffect(() => {
        if (initialized.current) return;

        setLearnSectionCode(subsection?.learnSectionCode ?? '');
        setImage(subsection?.image?.src ?? null);
        const preferredCode = subsection?.languageCode || null;
        const initialLangCode =
            (preferredCode && availableLanguageCodes.includes(preferredCode) && preferredCode) ||
            availableLanguageCodes[0] ||
            '';
        setLanguageCode(initialLangCode);

        if (editModeHasRows) {
            const pickedRow = languageRowByCode.get(initialLangCode) || null;
            setName(pickedRow?.name || '');
            setContent(pickedRow?.content || '');
        } else {
            setName('');
            setContent('');
        }

        initialized.current = true;
    }, [subsection, sectionsSorted, availableLanguageCodes, languageRowByCode, editModeHasRows]);

    const handleLanguageChange = (e) => {
        const selectedCode = e.target.value;
        setLanguageCode(selectedCode);
        if (editModeHasRows) {
            const pickedRow = languageRowByCode.get(selectedCode);
            setName(pickedRow?.name || '');
            setContent(pickedRow?.content || '');
        } else {
            setName('');
            setContent('');
        }
    };

    const renderLanguageOptions = () => {
        return availableLanguageCodes.map((code) => {
            const displayName = languageNameByCode.get(code) || code;
            return (
                <option key={code} value={code}>
                    {displayName} ({code})
                </option>
            );
        });
    };

    const toggleSite = (value) => {
        let sel = selectedSites;
        if (value === '') {
            sel = selectedSites.length === allSites.length ? [] : allSites.map(s => s.siteCode);
        } else if (sel.includes(value)) {
            sel = sel.filter(v => v !== value);
        } else {
            sel = sel.concat(value);
        }
        setSelectedSites([...sel]);
    };

    return (
        <div
            tabIndex="-1"
            className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full md:h-screen justify-center items-center flex bg-black/90 md:pb-12 pb-14"
            role="dialog"
        >
            <div className="bg-white rounded-2xl py-6 md:w-1/2 xl:w-2/5 w-11/12 max-w-full h-auto m-auto md:mt-12 mt-14 flex flex-col gap-4">
                <div className="flex items-center justify-between px-6">
                    <h2 className="text-gray-800 font-semibold md:text-xl text-lg leading-8 mb-0">
                        {type === 'add' ? 'Add' : 'Edit'} subcategory
                    </h2>
                    <button type="button" onClick={hideModal} className="inline-flex items-center justify-center w-6 h-6">
                        <span className="material-icons text-gray-600">close</span>
                    </button>
                </div>

                <div className="w-full py-2 md:px-6 px-4 flex items-start gap-6">
                    <form onSubmit={handleFormSubmit} className="flex w-full flex-col gap-3">
                        <div className="w-full">
                            <p className="font-semibold text-gray-800 text-lg mb-2">Image</p>
                            <LogoInput
                                handleLogoChange={(file) => setImage(file)}
                                removeImage={() => setImage(null)}
                                prefetchedLogo={image}
                            />
                        </div>

                        <label htmlFor="language" className="font-semibold text-gray-800 text-lg">
                            Language
                            <select
                                id="language"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer font-normal"
                                onChange={handleLanguageChange}
                                value={languageCode || ''}
                                required
                            >
                                <option value="">— Select language —</option>
                                {renderLanguageOptions()}
                            </select>
                        </label>

                        <label htmlFor="name" className="font-semibold text-gray-800 text-lg">
                            Name
                            <input
                                id="name"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                                onChange={(e) => setName(e.target.value)}
                                value={name || ''}
                                required
                            />
                        </label>

                        <label htmlFor="section" className="font-semibold text-gray-800 text-lg">
                            Category
                            <select
                                id="section"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer font-normal"
                                onChange={(e) => setLearnSectionCode(e.target.value)}
                                value={learnSectionCode || ''}
                                required
                            >
                                <option value="">— Select category —</option>
                                {sectionsSorted.map((sectionItem) => {
                                    const code = sectionItem?.learnSectionCode ?? '';
                                    return (
                                        <option key={code} value={code}>
                                            {sectionItem?.name || ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </label>

                        <label htmlFor="content" className="font-semibold text-gray-800 text-lg">
                            Content
                            <textarea
                                id="content"
                                rows="6"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                                onChange={(e) => setContent(e.target.value)}
                                value={content || ''}
                                required
                            />
                        </label>

                        <label htmlFor="site" className="font-semibold text-gray-800 text-lg">
                            Enabled on sites
                            <select
                                size="20"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.focus();
                                    const el = e.target;
                                    const { scrollTop } = el.parentNode;
                                    setTimeout(() => el.parentNode.scrollTo(0, scrollTop), 1);
                                    toggleSite(e.target.value);
                                }}
                                onChange={(e) => e.preventDefault()}
                                multiple
                                value={selectedSites}
                                id="site"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                            >
                                <option value="">All Sites</option>
                                {allSites?.map((site) => (
                                    <option key={site?.siteCode} value={site?.siteCode}>
                                        {site?.topLocation?.name} - ({site?.siteCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="w-full flex items-center justify-end">
                            <NewsfeedButton classNames="md:w-auto w-full" loading={posting} title="Submit" type="primary" />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

LearnSubsectionAdminModal.propTypes = {
    type: PropTypes.oneOf(['add', 'edit']),
    postSubsection: PropTypes.func,
    posting: PropTypes.bool.isRequired,
    preFetchedSubsection: PropTypes.string,
    subsectionLanguages: PropTypes.string,
    sections: PropTypes.string,
    languages: PropTypes.string,
    sites: PropTypes.string,
};

export default LearnSubsectionAdminModal;
